import classNames from "classnames";
import { chunk } from "lodash";
import AppContext from "PFApp/app_context";
import LazyLocalExportTrigger from "PFApp/components/document_buttons/lazy_local_export_trigger";
import FiltersBox from "PFApp/components/filters/filters_box";
import FiltersButton from "PFApp/components/filters/filters_button";
import FiltersPreview from "PFApp/components/filters/filters_preview";
import ProfileCardConnected from "PFApp/components/profile_card_connected";
import SortBy from "PFApp/components/sort_by";
import useIsChatEnabled from "PFApp/use_is_chat_enabled";
import { LoadingDots } from "PFComponents/loading_dots";
import NotFoundComponent from "PFComponents/not_found/not_found";
import { profileResponseShapePropType } from "PFCore/base/prop_types/profile_response_prop_types";
import { useDeterministicStringify } from "PFCore/helpers/use_deterministic_stringity";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { getExportProfiles } from "PFCore/services/export_profiles";
import NoProfilesIcon from "PFIcons/no_profiles.svg";
import { PermissionRule } from "PFTypes";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  checkFiltersAreClear,
  getAPIFilters,
  getValueFilters,
  handleRequestFiltersChange,
  handleRequestFiltersClear
} from "../../use_filtered_collection/format_filters";
import ShortlistForProfileButton from "../shortlist_for_profile/shortlist_for_profile_button";
import { Suggestions } from "./parts/suggestions";
import css from "./profiles_page.less";

const ProfilesPage = ({
  meta,
  loading,
  profiles,
  handleFiltersCollectionChange,
  handleOrderChange,
  pagination
}) => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const { t } = useTranslation("profiles");

  const stringify = useDeterministicStringify();
  const [filtersShown, setFiltersShown] = useState(false);
  const filtersRef = useRef(null);

  const isChatEnabled = useIsChatEnabled();

  const [requestFilters, setRequestFilters] = useState(getValueFilters(meta.filters));
  const [prevRequestFiltersKey, setPrevRequestFiltersKey] = useState(
    stringify(getAPIFilters(requestFilters))
  );

  const requestFiltersKey = stringify(getAPIFilters(requestFilters));
  useEffect(() => {
    if (prevRequestFiltersKey !== requestFiltersKey) {
      setPrevRequestFiltersKey(requestFiltersKey);
      setRequestFilters(requestFilters);
      handleFiltersCollectionChange(requestFilters);
    }
  }, [requestFiltersKey]);

  const canExportDocs = useIsPermittedTo(currentProfile)(PermissionRule.ExportDocuments);

  const filtersAreClear = checkFiltersAreClear(meta.filters);

  const handleFilterChange = (filter, value) => {
    setRequestFilters((requestFilters) => handleRequestFiltersChange(requestFilters, filter, value));
  };

  const handleFilterClear = () => {
    setRequestFilters((requestFilters) => handleRequestFiltersClear(requestFilters));
  };

  return (
    <div className={classNames(css.root, { [css.loading]: loading })}>
      <div className={css.wrapper}>
        <div className={css.title}>{t("list.profiles")}</div>
        {canExportDocs && profiles && (
          <LazyLocalExportTrigger
            quantity={profiles.length}
            getProfilesCollection={(paginationPage = 1, paginationSize = 50) => {
              const items = profiles.map(({ profile }) => profile.id);

              const paginatedArray = chunk(items, paginationSize);

              return getExportProfiles({
                profile_ids: paginatedArray[paginationPage - 1]
              }).then((resp) => ({
                entries: resp.map((profile) => ({
                  profile
                })),
                meta: { total: items.length, page: paginationPage }
              }));
            }}
          />
        )}
      </div>

      <div className={css.filtersWrap}>
        <div className={css.filtersPreviewWrap}>
          <FiltersPreview
            meta={meta}
            filtersAreClear={filtersAreClear}
            clearFilters={handleFilterClear}
            onFilterChange={handleFilterChange}
            onMoreClicked={() => setFiltersShown(true)}
          />
        </div>
        <div ref={filtersRef} style={{ display: "flex", flexDirection: "row" }}>
          <SortBy
            options={meta.order.options.map((option) => ({
              displayElement: option.text,
              id: option.text,
              item: option
            }))}
            sort={meta.order.value?.text}
            onSortChange={handleOrderChange}
            style={{ marginRight: 10 }}
          />
          <FiltersButton
            filtersShown={filtersShown}
            filtersAreClear={filtersAreClear}
            onClick={() => setFiltersShown(!filtersShown)}
            withLabel
          />
        </div>
      </div>

      {filtersShown && (
        <div style={{ position: "relative", height: 2, zIndex: 20, marginTop: -34 }}>
          <FiltersBox
            isLoading={loading}
            closeFilters={() => setFiltersShown(false)}
            filtersAreClear={filtersAreClear}
            meta={meta}
            clearFilters={handleFilterClear}
            handleFilterChange={handleFilterChange}
            style={{ marginBottom: 20, position: "absolute", width: "100%" }}
          />
        </div>
      )}

      <Suggestions filters={meta.filters} />

      {!profiles?.length ? (
        loading ? (
          <LoadingDots circlesEnabled pageViewEnabled />
        ) : (
          <NotFoundComponent message={t("list.noResultsMessage")} buttonMessage={t("list.goBack")}>
            <NoProfilesIcon />
            <h2>{t("list.noProfilesMatch")}</h2>
          </NotFoundComponent>
        )
      ) : (
        <>
          <ul className={css.list} data-qa-id="get-help-your-colleagues-list">
            {profiles.map((profileResponse) => (
              <li className={css.listItem} key={profileResponse.id}>
                <ProfileCardConnected
                  profileResponse={profileResponse}
                  moreInfoTrackEvent="profiles-list-profile-show"
                  actions={<ShortlistForProfileButton profile={profileResponse.profile} />}
                  withChat={isChatEnabled}
                  currentProfile={currentProfile}
                />
              </li>
            ))}
          </ul>
          {pagination && <div className={css.paginationWrapper}>{pagination}</div>}
        </>
      )}
    </div>
  );
};

ProfilesPage.propTypes = {
  profiles: PropTypes.arrayOf(profileResponseShapePropType),
  meta: PropTypes.shape({
    filters: PropTypes.shape({
      strings: PropTypes.object,
      numbers: PropTypes.object,
      fields: PropTypes.object,
      availability: PropTypes.object
    }),
    order: PropTypes.object,
    total: PropTypes.number
  }),
  handleOrderChange: PropTypes.func,
  handleFiltersCollectionChange: PropTypes.func,
  pagination: PropTypes.object,
  loading: PropTypes.bool
};

ProfilesPage.defaultProps = {};

export default ProfilesPage;
